import React, { useState, useRef, useEffect } from "react";
import Link from "@qlik/qlik/src/components/Link";
import Popup from "@qlik/qlik/src/components/Popup";
import VidyardLink from "@qlik/qlik/src/components/VidyardLink";
import { useModuleContext } from "@qlik/qlik/src/context/ModuleContext";
import { useHeaderContext } from "@qlik/qlik/src/context/HeaderContext";
import {
    getVidyardURL,
    appendQueryParamsToExternalUrl,
    internalUrl,
    addParamToURL,
    renderSanitizedHtmlContent,
} from "@qlik/qlik/src/lib/util/helpers";
import { renderRichText } from "gatsby-source-contentful/rich-text";
import formatContentfulRichText from "@qlik/qlik/src/lib/format-rich-text-content";
import mapButtonType from "./mapButtonType";
import { DOWNLOADABLE, NEW_TAB, POPUP } from "@qlik/qlik/src/lib/constants/cta-constants";
import isNoItem from "@qlik/qlik/src/lib/util/isNoItem";
import camelCase from "lodash.camelcase";

/**
 * Component to prompt download when startDownload is true
 * @param {Object} props - The properties passed to the component
 * @param {boolean} props.startDownload - Flag to start download
 * @param {Object} props.downloadData - Data for download
 * @param {Function} props.setTriggerDownload - Function to set triggerDownload state
 */
const PromptDownload = ({ startDownload, downloadData, setTriggerDownload }) => {
    const anchorRef = useRef();
    useEffect(() => {
        if (startDownload === true) {
            anchorRef.current.click();
            setTriggerDownload(false);
        }
    }, [startDownload, setTriggerDownload]);

    return (
        <a
            target="_blank"
            rel="noopener noreferrer"
            href={downloadData.url}
            download={downloadData.download}
            ref={anchorRef}
        >
            {" "}
        </a>
    );
};

/**
 * Cta component
 * @param {Object} props - The properties passed to the component
 * @param {Object} props.data - Data for the component
 * @param {string} props.className - Class name for the component
 * @param {ReactNode} props.children - Children of the component
 */
const Cta = ({ data, className, children }) => {
    const moduleContext = useModuleContext();
    const headerContext = useHeaderContext();

    const [downloadData, setDownloadData] = useState(null);
    const [triggerDownload, setTriggerDownload] = useState(false);
    const downloadURL = data?.internalLink?.asset
        ? data?.internalLink?.asset[0]?.original_secure_url
        : data?.externalUrl;
    const [...parts] = downloadURL ? downloadURL.split("/") : [];
    const downloadFileName = parts?.pop();

    // Fetch data?.contentful_id from the data object
    // take the last 4 characters of the contentful_id
    // and append it to the moduleClass
    // Example data.contentful_id = '1234567890abcdef'
    // 'abcdef' will be appended to the moduleClass
    const contentfulIdSlice = data?.contentful_id?.slice(-4);

    // Generating the className based on the module context
    const moduleClass = moduleContext
        ? `cta-module-${moduleContext?.i + 1}-${camelCase(moduleContext?.frontEndComponent)}--${
              contentfulIdSlice ? contentfulIdSlice : ""
          }`
        : "";

    // If the header context is not null, use it to generate the className
    const headerClass = headerContext ? `cta-header--${contentfulIdSlice ? contentfulIdSlice : ""}` : "";

    // Concatenate the moduleClass and headerClass
    const contextClass = `${moduleClass} ${headerClass}`;

    // Return Null if it's a "No Item" so the CTA doesn't render anything
    if (isNoItem(data?.text)) return null;

    /**
     * Function to handle download
     * @param {Event} e - The event object
     */
    const handleDownload = (e) => {
        e.preventDefault();
        fetch(downloadURL, {
            method: "GET",
        })
            .then((response) => {
                if (response.ok) {
                    return response.blob();
                }
                throw new Error("Download failed");
            })
            .then((blob) => {
                const downloadDataObj = {
                    url: window.URL.createObjectURL(new Blob([blob])),
                    download: downloadFileName,
                };
                setDownloadData(downloadDataObj);
                setTriggerDownload(true);
            })
            .catch(() => {});
    };

    // If the data only contains text, return a span with the text
    if (data?.textOnly) return <span className={className}>{data?.text}</span>;

    const internalLink = internalUrl(data?.internalLink, data?.queryParams);

    // construct href attribute value based on settings
    let href = appendQueryParamsToExternalUrl(data?.externalUrl, data);

    if (data?.content && data?.linkBehavior === POPUP) {
        href = `#${data.id}`;
    } else if (data?.maskUrl?.maskUrl) {
        href = data?.maskUrl?.maskUrl;
    } else if (data?.internalLink) {
        href = internalLink;
    }

    const doesntHaveUrl =
        typeof href === "undefined" || (href === "/undefined" && typeof data?.externalUrl === "undefined");

    // construct usual anchor only tag props
    const linkProps = {
        newWindow:
            data?.browsingContext === "New Tab" ? true : data?.browsingContext === "Same Tab" ? false : undefined,
        className: `${mapButtonType(data?.type)} ${className || ""} ${
            doesntHaveUrl || href === null ? "pointer-events-none" : ""
        } highlight-transparent ${contextClass}`,
    };

    // @TODO remove data-test attributes once links are stable.

    // Handle Vidyard.
    const vidyardVideoUrl = getVidyardURL(data?.externalUrl);
    if (vidyardVideoUrl) {
        const vidyardVideoUrlWithAutoplay = addParamToURL(vidyardVideoUrl, "autoplay", "1");

        const anchorProps = {
            className: `${linkProps.className} ${contextClass}`,
            target: data?.browsingContext === NEW_TAB ? "_blank" : "_self",
            rel: data?.browsingContext === NEW_TAB ? "noreferrer" : undefined,
            ...(data?.linkBehavior === POPUP
                ? { "data-fancybox": "", "data-src": vidyardVideoUrlWithAutoplay, href: "#" }
                : { href: vidyardVideoUrlWithAutoplay }),
            "data-type": vidyardVideoUrl && !data?.content && data?.linkBehavior === POPUP ? "iframe" : undefined,
            "data-iframe":
                vidyardVideoUrl && !data?.content && data?.linkBehavior === POPUP ? '{"preload":false}' : undefined,
        };
        return (
            <VidyardLink
                data-test="0"
                vidyardVideoUrl={vidyardVideoUrl}
                linkText={data.text}
                linkProps={linkProps}
                anchorProps={anchorProps}
                children={children}
            />
        );
    }

    // Create the default link.
    let defaultLink;
    if (children) {
        defaultLink = (
            <Link data-test="1" data-href={href} to={href} {...linkProps}>
                {children}
            </Link>
        );
    } else if (data?.content?.raw && data?.text === null) {
        defaultLink = (
            <Link data-test="2" to={href} {...linkProps}>
                {renderRichText(data?.content, formatContentfulRichText())}
            </Link>
        );
    } else {
        defaultLink = (
            <Link
                data-test="3"
                data-href={href}
                to={href}
                {...linkProps}
                dangerouslySetInnerHTML={{ __html: renderSanitizedHtmlContent(data?.text) }}
            />
        );
    }

    // Handle Downloads and Popups
    if (data?.linkBehavior === DOWNLOADABLE || data?.linkBehavior === POPUP) {
        const anchorProps = {
            ...linkProps,
            ...(data?.linkBehavior === POPUP ? { "data-fancybox": "", "data-src": href, href: "#" } : { href }),
            ...(data?.linkBehavior === DOWNLOADABLE ? { onClick: handleDownload } : {}),
            download: data?.linkBehavior === DOWNLOADABLE ? "" : undefined,
        };

        // handle downloads & popups
        return (
            <>
                <a
                    data-test="4"
                    to={href}
                    {...anchorProps}
                    dangerouslySetInnerHTML={{ __html: renderSanitizedHtmlContent(data?.text) }}
                />
                {data?.content && <Popup data={data} />}
                {triggerDownload && (
                    <PromptDownload
                        startDownload={triggerDownload}
                        downloadData={downloadData}
                        setTriggerDownload={setTriggerDownload}
                    />
                )}
            </>
        );
    }

    return defaultLink;
};

export default Cta;
